import * as _ from 'underscore';
import { SiteMembershipGridColumns } from '../../app/content/SiteMembershipGridColumns';
import { Messages } from '../../codegen/Localize';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { AngularToReactDataGridBridge } from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { ReactCheckboxTristate } from '../../common/react/components/ReactCheckboxTristate';
import { SiteMembershipFilters } from '../../common/react/components/SiteMembershipFilters';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import { UserAgentSupport } from '../../common/utilities/UserAgentSupport';
import { AngularContext, AngularContextFactory } from '../factories/AngularContextFactory';
import { ListResult } from '../fetchers/ListResult';
import { SubplaceWithActionButtonsScope } from '../helpers/ActionButtonsHelper';
import { Filter } from '../helpers/Filter';
import { SliceableHelper } from '../helpers/SliceableHelper';
import { IColumnWidths } from '../models/ColumnWidths';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { ISliceable } from '../models/ISliceable';
import { Module } from '../Module';
import { SiteNameWithIds } from '../resources/SiteNameWithIds';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { ISiteNameWithId } from '../ServerApi/types/ISiteNameWithId';
import { ISiteRole, SiteRoles } from '../ServerApi/types/ISiteRole';
import { IUserRole } from '../ServerApi/types/IUserRole';
import { ServerService } from '../services/ServerService';
import { ISiteRoleChange, IUsersWithSiteRolesModel } from '../services/SiteRolesPickerClientService';
import { SliceableFactory } from '../services/SliceableFactory';
import { SubplaceWithDataGridScope } from './ContentSubplaceCtrl';

export interface ISiteRoleChanges {
  siteRoles: _.Dictionary<ISiteRoleChange>; // changes keyed by siteId
  serverAdmin: string; // CheckboxState
}

export interface SiteRoleSelectorPropsDictionary {
  [siteId: string]: SiteRolePickerMenu.Props;
}

export interface SiteRoleCheckboxPropsDictionary {
  [siteId: string]: ReactCheckboxTristate.Props;
}

export interface SiteIdToSiteDictionary {
  [siteId: string]: ISiteNameWithId;
}

export let SiteMembershipDialogFilters = {
  AllSites: 'siteMembershipAllSites',
  AssignedSites: 'siteMembershipAssignedSites',
  SearchResults: 'siteMembershipSearchResults'
};

export interface ISiteRolesPickerScope extends ng.IScope,
SubplaceWithActionButtonsScope<ISiteNameWithId, {}>,
SubplaceWithDataGridScope<ISiteNameWithId> {
  adminCheckboxClicked(): void;
  adminCheckboxState(): string;
  adminCheckboxProps: ReactCheckboxTristate.Props;
  siteCheckboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props;
  busy: boolean;
  changes: ISiteRoleChanges;
  checkboxClicked(site: ISiteNameWithId, colIndex?: string): void;
  checkboxState(site: ISiteNameWithId): string;
  isRowDirty(site: ISiteNameWithId): boolean;
  modifySiteRolesDisabled(): boolean;
  setSiteRole(site: ISiteNameWithId, siteRole: string): void;
  siteNames: ISliceable<ISiteNameWithId>;
  sliceable: ISliceable<ISiteNameWithId>;
  siteRole(site: ISiteNameWithId): string;
  lastSiteFilter: string;
  needsAssignedSitesRefresh: boolean;
  refreshAssignedSites: () => void;
  emptyTemplateModel: IEmptyTemplateModel;
  columnWidths: IColumnWidths;
  //siteRoleOptions: string[];
  // siteRoleOptions are replaced with selectCtrl below for easier testing, this should be removed when tb:select
  // is added to siteRolesPicker.pug
  selectCtrl: {
    options: string[];
  };
  dropdownProps: (site: ISiteNameWithId) => SiteRolePickerMenu.Props;
  siteToDropdownPropMap: SiteRoleSelectorPropsDictionary;
  shouldShowLicensingTierInfo: boolean;
  usersWithSiteRoles: IUsersWithSiteRolesModel;
  siteMembershipFiltersProps: SiteMembershipFilters.Props;
}

interface IUserCountsAndRolesBySiteId extends _.Dictionary<{
  count: number;
  siteRole: string;
}> { }

export class SiteRolesPickerCtrl {

  public static $inject = [
    '$scope',
    '$translate',
    'AngularContextFactory',
    'AngularToReactDataGridBridge',
    'ServerService',
    'SiteRoles',
    'SiteRoleOptionsResource',
    'SiteNames',
    'SliceableFactory'];

  // site role and user count aggregated by siteId.
  private userCountsAndRolesBySiteId: IUserCountsAndRolesBySiteId;
  private numSelectedUsers: number;
  private numSelectedServerAdmins: number;
  private originalAdminState: string; // CheckboxState
  private uncheckedAssignedSites: number; //to keep track of whether or not we need to show the refresh button in Assigned.
  private siteFilters: string[];
  private siteFilter: SiteRolesPickerCtrl.IFilter;
  private searchInput: SiteRolesPickerCtrl.ISearchInput;

  private boundHandleCancelSearch = this.handleCancelSearch.bind(this);
  private boundHandleSearchInputTextChange = this.handleSearchInputTextChange.bind(this);
  private boundSelectFilter = this.selectFilter.bind(this);
  private siteToCheckboxPropMap: SiteRoleCheckboxPropsDictionary;
  private siteIdToSiteMap: SiteIdToSiteDictionary;
  private angularContext: AngularContext;

  constructor(
    private $scope: ISiteRolesPickerScope,
    private $translate: ng.translate.ITranslateService,
    private AngularContextFactory: AngularContextFactory,
    private AngularToReactDataGridBridge: AngularToReactDataGridBridge,
            serverService: ServerService,
            siteRoles: SiteRolesResource,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private siteNamesResource: SiteNameWithIds,
    private sliceableFactory: SliceableFactory) {

    this.uncheckedAssignedSites = 0;
    this.siteFilter = { selection: SiteMembershipDialogFilters.AllSites };
    this.userCountsAndRolesBySiteId = {};
    this.searchInput = { text: '' };
    if (!$scope.changes) {
      $scope.changes = {
        siteRoles: {},
        serverAdmin: undefined
      };
    }

    $scope.siteToDropdownPropMap = {};
    this.siteToCheckboxPropMap = {};
    this.siteIdToSiteMap = {};

    $scope.emptyTemplateModel = {
      title: 'noAssignedSites',
      action: () => {
        $scope.$apply(() => {
          this.siteFilter.selection = SiteMembershipDialogFilters.AllSites;
          this.updateSiteFilterSelection(this.siteFilter.selection);
        });
      }
    };

    $scope.needsAssignedSitesRefresh = false;

    $scope.siteNames = this.siteNamesResource.all();
    $scope.sliceable = $scope.siteNames;

    $scope.columnWidths = {
      getActual: this.getColumnWidths,
      getMin: this.getColumnWidths,
      hasPreferredWidths: () => { return true; }
    };

    $scope.adminCheckboxClicked = () : void => {
      $scope.$apply(() => {

        const newState: string = CheckboxState.toggle(this.$scope.adminCheckboxState());

        if (this.siteFilter.selection === SiteMembershipDialogFilters.AssignedSites) {

          //all selected -> empty. Use each checkbox's state without admin information to determine what should be in the new slice.
          if (newState === CheckboxState.empty) {
            this.getAssignedSitesList().then((slice: ISliceable<ISiteNameWithId>) => {
              $scope.sliceable = slice;
              this.updateAdminState(newState);
            });
          } else { //empty -> all selected.
            this.updateAdminState(newState);
            $scope.refreshAssignedSites();
          }

        } else {
          this.updateAdminState(newState);
        }
    });
    };

    $scope.$watch((scope: ISiteRolesPickerScope) => scope.changes.serverAdmin, () => {
      this.updateAllSitesDropdownProps();
      this.updateAllSitesCheckboxProps();
      this.$scope.adminCheckboxProps = {
        ...this.$scope.adminCheckboxProps,
        checkboxState: this.$scope.adminCheckboxState()
      };
      let angularContext = this.AngularContextFactory.create(this.$scope);
      this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
    });

    $scope.adminCheckboxState = (): string => {
      if ($scope.changes.serverAdmin) {
        return $scope.changes.serverAdmin;
      }
      return this.originalAdminState;
    };

    $scope.checkboxState = (site: ISiteNameWithId): string => {
      // if server admin is checked, all sites should be checked
      if ($scope.adminCheckboxState() === CheckboxState.checked) {
        return CheckboxState.checked;
      }

      // if some of the selected users are server admins, site role is always mixed
      if ($scope.adminCheckboxState() === CheckboxState.mixed) {
        return CheckboxState.mixed;
      }

      return this.checkboxStateWithoutAdmin(site);
    };

    $scope.siteRole = (site: ISiteNameWithId) => {
      // if server admin is checked, all site roles should be server admin
      if ($scope.adminCheckboxState() === CheckboxState.checked) {
        return SiteRoles.ServerAdministrator;
      }

      // if some of the selected users are server admins, site role is always mixed
      if ($scope.adminCheckboxState() === CheckboxState.mixed) {
        return SiteRolesResource.Mixed;
      }

      // local changes override what we have from the server
      if ($scope.changes.siteRoles[site.id] &&
        $scope.changes.siteRoles[site.id].siteRole) {
        return $scope.changes.siteRoles[site.id].siteRole;
      }

      const siteInfo = this.userCountsAndRolesBySiteId[site.id];
      const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;

      if (numSelectedSiteMembers === this.numSelectedUsers) {
        // if all of the selected users are in all the sites, use the aggregated role
        return siteInfo.siteRole;
      } else if (numSelectedSiteMembers > 0) {
        // some of the selected users are site members.
        return SiteRolesResource.Mixed;
      } else {
        // numSelectedSiteMembers === 0
        return SiteRoles.Unlicensed;
      }
    };

    //$scope.siteRoleOptions = SiteRoles.WithoutGuestOrServerAdmin;
    // TODO: Replace siteRoleOptions back once a tb:select component is added back to siteRolesPicker.pug
    $scope.selectCtrl = {
      options: siteRoles.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator)
    };

    $scope.shouldShowLicensingTierInfo = serverService.shouldShowServerLicensingTierInfo();

    $scope.dropdownProps = (site: ISiteNameWithId): SiteRolePickerMenu.Props => {
      if (!$scope.siteToDropdownPropMap[site.id]) {
        this.initDropdownPropsUsingRowInfo(site);
      }

      if ($scope.adminCheckboxState() === CheckboxState.checked) {
        $scope.siteToDropdownPropMap[site.id].siteRoleOptions = siteRoleOptionsResource.getAllSiteRoleOptions();
      } else {
        $scope.siteToDropdownPropMap[site.id].siteRoleOptions =
          siteRoleOptionsResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
      }

      return $scope.siteToDropdownPropMap[site.id];
    };

    $scope.adminCheckboxProps = {
      clicked: this.$scope.adminCheckboxClicked,
      disabled: $scope.busy,
      label: Messages.serverAdminSelect(),
      checkboxState: this.originalAdminState,
    };

    $scope.$watch('busy', () => {
      this.$scope.adminCheckboxProps = {
        ...this.$scope.adminCheckboxProps,
        disabled: $scope.busy
      };
      this.updateAllSitesCheckboxProps();
    });

    $scope.$watch('modifySiteRolesDisabled()', () => {
      this.updateAllSitesCheckboxProps();
    });

    $scope.$watch('sliceable', () => {
      this.updateReactDataGrid();
    });

    $scope.$watch('emptyTemplateModel.title', () => {
      $scope.dataGridProps.emptyChild = this.AngularToReactDataGridBridge.emptyTemplateElementForNoAssignedSites($scope.emptyTemplateModel);
    });

    $scope.siteCheckboxProps = (site: ISiteNameWithId) => {
      if (!this.siteToCheckboxPropMap[site.id]) {
        this.siteIdToSiteMap[site.id] = site;
        return this.initCheckboxPropsUsingRowInfo(site);
      }
      return this.siteToCheckboxPropMap[site.id];
    };

    $scope.checkboxClicked = (site: ISiteNameWithId, colIndex?: string) => {
      $scope.$apply(() => {
      //if the site role select menu is visible, clicking on the column should not toggle the row.
      const roleSelectColumnIndex = '2';
      if((colIndex === roleSelectColumnIndex && $scope.checkboxState(site) !== CheckboxState.empty) || $scope.modifySiteRolesDisabled()) {
        return;
      }

      const siteId = site.id;

      if (!$scope.changes.siteRoles[siteId]) {
        $scope.changes.siteRoles[siteId] = { siteName: site.name };
      }

      const siteRoleChange = $scope.changes.siteRoles[siteId];

      if (this.siteFilter.selection === SiteMembershipDialogFilters.AssignedSites) {

        if (this.$scope.checkboxState(site) === CheckboxState.empty) {
          this.uncheckedAssignedSites--;
        } else if (this.$scope.checkboxState(site) === CheckboxState.checked) {
          this.uncheckedAssignedSites++;
        }

        $scope.needsAssignedSitesRefresh = this.uncheckedAssignedSites > 0;
      }

      siteRoleChange.checkboxState = CheckboxState.toggle(this.$scope.checkboxState(site));

      // if the user has unchecked the checkbox, or if the user is adding the user to the site but hasn't picked a role yet,
      // set the site role to the default site role
      if (siteRoleChange.checkboxState === CheckboxState.empty || !siteRoleChange.siteRole) {
        siteRoleChange.siteRole = DefaultSiteRole;
      }

      // we only want to keep changes that differ from the original state (server state).
      // if the checkbox-click returned the checkbox to its original state, remove the locally stored change.
      // otherwise leave the change.
      if (this.getOriginalCheckboxStateForSite(siteId) === this.$scope.checkboxState(site)) {
        delete $scope.changes.siteRoles[siteId];
      }

      // update dropdown props selected role so that latest site role selection changes are reflected
      $scope.dropdownProps(site).selectedRole = siteRoleOptionsResource.getSiteRoleOptionBySiteRole($scope.siteRole(site));

      const siteToCheckboxPropsForSite = this.siteToCheckboxPropMap[site.id];
      this.siteToCheckboxPropMap[site.id] = {
        ...siteToCheckboxPropsForSite,
        checkboxState: this.$scope.checkboxState(site)
      };
      let angularContext = this.AngularContextFactory.create(this.$scope);
      this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
     });
    };

    $scope.setSiteRole = (site: ISiteNameWithId, role: string) => {
      const siteId = site.id;
      $scope.changes.siteRoles[siteId] = {
        siteRole: role,
        checkboxState: CheckboxState.checked,
        siteName: site.name
      };
    };

    // $note-ltan-2014-2-23:
    // Even though we no longer allow changing user selection while the panel is open,
    // this watch is still needed to give the parent of the SiteRolesPicker some time
    // to compute the site roles. This watch will then be able to pick them up.
    $scope.$watch('usersWithSiteRoles', () => {
      this.updateModel();
    });

    $scope.refreshAssignedSites = () => {
      $scope.needsAssignedSitesRefresh = false;
      if (this.$scope.adminCheckboxState() !== CheckboxState.empty) {
        this.$scope.sliceable = $scope.siteNames;
      } else {
        this.getAssignedSitesList().then((slice: ISliceable<ISiteNameWithId>) => {
          this.$scope.sliceable = slice;
        });
      }
    };

    $scope.isRowDirty = (site: ISiteNameWithId) => {
      return !!$scope.changes.siteRoles[site.id] && ($scope.adminCheckboxState() === CheckboxState.empty);
    };

    $scope.modifySiteRolesDisabled = (): boolean => {
      return ($scope.adminCheckboxState() !== CheckboxState.empty);
    };

    $scope.$watch('searchInput.text', (text: string) => {
      this.searchInputTextChangeToProps(text);
    });

    this.updateSiteFilterSelection(this.siteFilter.selection);

    $scope.siteMembershipFiltersProps = {
      filters: this.siteFilters,
      onFilterSelected: this.boundSelectFilter,
      siteFilterSelection: this.siteFilter.selection,
      onSearchInputTextChange: this.boundHandleSearchInputTextChange,
      onCancelSearch: this.boundHandleCancelSearch,
      searchInputText: this.searchInput.text,
      translate: $translate.instant
    };

    this.configureForReactDataGrid();

    // This call is important. It computes the initial state of things.
    this.updateModel();

    this.angularContext = AngularContextFactory.create(this.$scope);
  }

  private updateSiteFilterSelection(newFilter: string): void {
    this.updateSiteFilters();
    if (newFilter === SiteMembershipDialogFilters.AssignedSites) {
      this.$scope.refreshAssignedSites();
    }

    if (newFilter === SiteMembershipDialogFilters.AllSites) {
      this.$scope.sliceable = this.$scope.siteNames;
    }

    if (newFilter !== SiteMembershipDialogFilters.SearchResults) {
      this.$scope.lastSiteFilter = newFilter;
      this.searchInput.text = '';
    }
    this.$scope.needsAssignedSitesRefresh = false;
  }

  private selectFilter(selectedFilter: string): void {
    this.$scope.$apply(() => {
      this.siteFilter.selection = selectedFilter;
      this.updateSiteFilterSelection(selectedFilter);
    });
  }

  private submitSearch(searchText: string, $event?: JQueryEventObject): void {
    this.$scope.emptyTemplateModel.title = 'noMatchingResults';
    if (!searchText) {
      this.siteFilter.selection = this.$scope.lastSiteFilter;
      this.updateSiteFilterSelection(this.$scope.lastSiteFilter);
    } else {
      if (this.siteFilter.selection !== SiteMembershipDialogFilters.SearchResults) {
        this.siteFilter.selection = SiteMembershipDialogFilters.SearchResults;
        this.updateSiteFilterSelection(SiteMembershipDialogFilters.SearchResults);
      }
      this.$scope.sliceable = this.siteNamesResource.all().withFilters([Filter.search(searchText)]);
    }

    if (UserAgentSupport.isTouch() && $event) {
      // blur to retract the keyboard when search is explicitly submitted
      $($event.target).trigger('blur');
    }
  }

  private handleSearchInputTextChange(searchInput: string): void {
    this.$scope.$apply(() => {
      this.searchInputTextChangeToProps(searchInput);
    });
  }

  private handleCancelSearch(): void {
    this.$scope.$apply(() => {
      this.searchInputTextChangeToProps('');
    });
  }

  private searchInputTextChangeToProps(searchInput: string): void {
    this.$scope.siteMembershipFiltersProps = {
      ...this.$scope.siteMembershipFiltersProps,
      searchInputText: searchInput
    };
    this.searchInput.text = searchInput;
    this.submitSearch(searchInput);
  }

  // Get the original/current state of server admins of the users.
  public static adminStateFromUsers(adminCount: number, totalUsersCount: number): string {
    if (adminCount === 0) {
      return CheckboxState.empty;
    } else if (adminCount < totalUsersCount) {
      return CheckboxState.mixed;
    } else {
      return CheckboxState.checked;
    }
  }

  private initDropdownPropsUsingRowInfo(site: ISiteNameWithId): void {
    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      this.$scope.$apply(() => {
        this.$scope.siteToDropdownPropMap[site.id].selectedRole = roleOption;
        this.$scope.setSiteRole(site, roleOption.role);
      });
    };

    const siteRoleOptions = this.siteRoleOptionsResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

    const props = <SiteRolePickerMenu.Props> {
      translate: this.$translate.instant,
      siteRoleOptions: siteRoleOptions,
      getSiteRoleOptionBySiteRole: (siteRole: string) => this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRole),
      selectedRole: this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(this.$scope.siteRole(site)),
      onOptionSelected: onSiteRoleSelected,
      isDisabled: this.$scope.modifySiteRolesDisabled()
    };

    this.$scope.siteToDropdownPropMap[site.id] = props;
  }

  private initCheckboxPropsUsingRowInfo(site: ISiteNameWithId): ReactCheckboxTristate.Props {
    const props: ReactCheckboxTristate.Props = {
      checkboxState: this.$scope.checkboxState(site),
      disabled: this.$scope.busy || this.$scope.modifySiteRolesDisabled(),
      clicked: () => {}
    };

    this.siteToCheckboxPropMap[site.id] = props;
    return props;
  }

  private updateAllSitesCheckboxProps(): void {
    for(let siteCheckboxProp in this.siteToCheckboxPropMap){
      let siteCheckboxPropItems = this.siteToCheckboxPropMap[siteCheckboxProp];
      this.siteToCheckboxPropMap[siteCheckboxProp] = {
        ...siteCheckboxPropItems,
        disabled: this.$scope.busy || this.$scope.modifySiteRolesDisabled(),
        checkboxState: this.$scope.checkboxState(this.siteIdToSiteMap[siteCheckboxProp])
      };
    }
    let angularContext = this.AngularContextFactory.create(this.$scope);
    this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
  }

  private updateAllSitesDropdownProps(): void {
    _.each(this.$scope.siteToDropdownPropMap, (props: SiteRolePickerMenu.Props, siteId: string) => {
      props.isDisabled = this.$scope.modifySiteRolesDisabled();
      const newSiteRole = this.$scope.siteRole({ id: siteId, luid: null, urlName: null } as ISiteNameWithId);
      props.selectedRole = this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(newSiteRole);
    });
    let angularContext = this.AngularContextFactory.create(this.$scope);
    this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
  }

  private updateAdminState(newState: string) {
    if (newState === this.originalAdminState) {
      this.$scope.changes.serverAdmin = undefined;
    } else {
      this.$scope.changes.serverAdmin = newState;
    }
  }

  private checkboxStateWithoutAdmin(site: ISiteNameWithId): string {
    // local changes override what we have from the server
    if (this.$scope.changes.siteRoles[site.id] &&
      this.$scope.changes.siteRoles[site.id].checkboxState) {
      return this.$scope.changes.siteRoles[site.id].checkboxState;
    }

    const siteInfo = this.userCountsAndRolesBySiteId[site.id];
    const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;

    if (numSelectedSiteMembers === this.numSelectedUsers) {
      // all selected users are members of this site.
      return CheckboxState.checked;
    } else if (numSelectedSiteMembers > 0) {
      // some users are members of the site.
      return CheckboxState.mixed;
    } else {
      // numSelectedSiteMembers === 0
      return CheckboxState.empty;
    }
  }

  //The second column's width is dependent on whether or not the scroll bar is visible.
  private getColumnWidths(index: number): number {
    const resizeableColumnIndex = 2;
    //checkbox column and group name column are constant. These should be kept in sync with the design spec for the dialog.
    const widths = [40, 160];
    const calcMemberCountWidth = $('.tb-picker-container .tb-data-grid-body-background').width() - widths[0] - widths[1];

    return index === resizeableColumnIndex ? calcMemberCountWidth : widths[index];
  }

  private updateSiteFilters() {
    const filters = [
      SiteMembershipDialogFilters.AllSites,
      SiteMembershipDialogFilters.AssignedSites
    ];

    if (this.siteFilter.selection === SiteMembershipDialogFilters.SearchResults) {
      filters.push(SiteMembershipDialogFilters.SearchResults);
    }

    this.siteFilters = filters;
    this.$scope.siteMembershipFiltersProps = {
      ...this.$scope.siteMembershipFiltersProps,
      filters: this.siteFilters,
      siteFilterSelection: this.siteFilter.selection
    };
  }

  private getAssignedSitesList(): ng.IPromise<ISliceable<ISiteNameWithId>> {
    return SliceableHelper.fetchAll(this.siteNamesResource.all())
      .then((sites: ListResult<ISiteNameWithId>) => {
        const assignedList =  _.filter(sites.items, (site: ISiteNameWithId) => this.checkboxStateWithoutAdmin(site) !== CheckboxState.empty);

        this.uncheckedAssignedSites = 0;
        this.$scope.emptyTemplateModel.title = 'noAssignedSites';
        return this.sliceableFactory.fromArray(assignedList);
      });
  }

  private updateModel() {
    if (this.$scope.usersWithSiteRoles) {
      this.updateSelectedUserCounts();
      this.userCountsAndRolesBySiteId =
        SiteRolesPickerCtrl.calculateUserCountsAndRolesBySite(this.$scope.usersWithSiteRoles.userRoles);
    } else {
      this.numSelectedUsers = 1;
      this.numSelectedServerAdmins = 0;
      this.userCountsAndRolesBySiteId = {};
    }

    // If server admin change state is the same as that of the current user(s),
    // it really means there is no change.
    this.originalAdminState = SiteRolesPickerCtrl.adminStateFromUsers(this.numSelectedServerAdmins, this.numSelectedUsers);
    if (this.originalAdminState === this.$scope.changes.serverAdmin) {
      this.$scope.changes.serverAdmin = undefined;
    }
    this.updateAllSitesDropdownProps();
    this.updateAllSitesCheckboxProps();
    this.$scope.adminCheckboxProps = {
      ...this.$scope.adminCheckboxProps,
      checkboxState: this.$scope.adminCheckboxState()
    };
    let angularContext = this.AngularContextFactory.create(this.$scope);
    this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
  }

  private updateReactDataGrid(){
    this.AngularToReactDataGridBridge.updateReactDataGridRowStore({scope: this.$scope, sliceable: this.$scope.sliceable});
    let angularContext = this.AngularContextFactory.create(this.$scope);
    this.$scope.dataGridProps.cols = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
  }

  private static calculateUserCountsAndRolesBySite(userRoles: IUserRole[]): IUserCountsAndRolesBySiteId {
    const userCountsAndRolesBySiteId: IUserCountsAndRolesBySiteId = {};

    _.each(userRoles, (userRole: IUserRole) => {
      _.each(userRole.sites, (site: ISiteRole) => {
        const siteUserInfo = userCountsAndRolesBySiteId[site.siteId];
        if (siteUserInfo) {
          ++siteUserInfo.count;
          if (siteUserInfo.siteRole !== site.siteRole) {
            siteUserInfo.siteRole = SiteRolesResource.Mixed;
          }
        } else {
          userCountsAndRolesBySiteId[site.siteId] = {
            count: 1,
            siteRole: site.siteRole
          };
        }
      });
    });

    return userCountsAndRolesBySiteId;
  }

  private updateSelectedUserCounts(): void {
    this.numSelectedUsers = this.$scope.usersWithSiteRoles.users.size();
    this.numSelectedServerAdmins = this.$scope.usersWithSiteRoles.serverAdmins.length;
  }

  // input a site ID, returns the initial checkbox state for the given site (checked, empty, or mixed)
  private getOriginalCheckboxStateForSite(siteId: string): string {
    if (!this.userCountsAndRolesBySiteId[siteId]) {
      return CheckboxState.empty;
    } else if (this.userCountsAndRolesBySiteId[siteId].count === this.$scope.usersWithSiteRoles.users.size()) {
      return CheckboxState.checked;
    } else {
      return CheckboxState.mixed;
    }
  }

  private configureForReactDataGrid() {
      let angularContext = this.AngularContextFactory.create(this.$scope);
      const columns = SiteMembershipGridColumns.columns(this.$scope.dropdownProps, this.$scope.siteCheckboxProps, this.getColumnWidths, angularContext);
      this.AngularToReactDataGridBridge.attachReactDataGridToScope({
        $scope: this.$scope,
        columns,
        emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementForNoAssignedSites(this.$scope.emptyTemplateModel),
        canShowFilterPanel: false
      });
      this.$scope.dataGridProps.onCellClick = (row: any, rowIndex: string) => {this.$scope.checkboxClicked(row, rowIndex);};
      this.$scope.dataGridProps.onRowClick = (row: any) => {this.$scope.checkboxClicked(row.data);};
      this.$scope.dataGridProps.showHorizontalGridLines = false;
  }
}

export module SiteRolesPickerCtrl {

  export interface IFilter {
    selection: string;
  }

  export interface ISearchInput {
    text: string;
  }

}

Module.instance().controller('SiteRolesPickerCtrl', SiteRolesPickerCtrl);
